import React, { useContext, memo, useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  Marker,
  MarkerClusterer
} from "@react-google-maps/api";
import { toast } from "react-toastify";
import { SEARCH_AREA_STATES, clusterDefaultZoomLvl, googleClusterImgUrl, siteOprtTowerSvg } from "../../../../constants/WebMap";
import { WebMapContext } from "../WebMapGoogleMap";
import { filterOperatorSiteTowers } from "../../../../services/api/siteService";
import { paginatedApiInvoker } from "../../../../utils/webMapApiHelper";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";

const SiteIcon = ({ idx, item, operatorDetails, optr, setTooltip, mapTool, cluster = null }) => {
  return (
    <>
      <Marker
        key={idx}
        position={{ lat: item.latitude, lng: item.longitude }}
        onLoad={(marker) => {
          const customIcon = (opts) =>
            Object.assign(
              {
                path: siteOprtTowerSvg,
                fillOpacity: 1,
                strokeWeight: 0.7,
                scale: 0.75,
                anchor: new window.google.maps.Point(20, 34),
              },
              opts
            );
          const operator = operatorDetails.find(
            (x) => x.operator === optr
          );

          marker.setIcon(
            customIcon({
              fillColor: operator && !operator?.isAnonymized ? operator.color : "#707070", // default #707070
              strokeColor: "white",
            })
          );
        }}
        onClick={() => {
          setTooltip({
            siteName: item.siteName,
            latitude: item.latitude,
            longitude: item.longitude,
          });
        }}
        options={{
          clickable: mapTool ? false : true
        }}
        clusterer={cluster}
      />
    </>
  )
}

const OperatorSitesSection = ({
  mapTool,
  operatorSiteVisible,
  searchAreaBtn,
  setSearchAreaBtn,
  setApisOngoing,
  setOperatorSiteTooltipData,
  saveLastBound,
  operatorsPermittedOpSite
}) => {
  let cancelToken;
  let timeOutForApiCall = 600;
  const timeOutForOperatorSiteTowers = useRef();

  const {
    eyeIcons,
    operatorDetails,
    selectedOperatorSiteOperator,
    mapZoomLevel,
    appliedFilters,
    siteZoom,
    getBbox,
    geohashNE,
    geohashSW,
    opSiteAreaToggle,
    boundsChanged,
    geohashZoom,
    downloadData, 
    setDownloadData,
  } = useContext(WebMapContext);

  const [operatorSiteTowers, setOperatorSiteTowers] = useState([]);

  useEffect(() => {
    if (mapZoomLevel < Math.min(siteZoom, clusterDefaultZoomLvl)) resetData();
  }, [mapZoomLevel, siteZoom]);

  useEffect(() => {
    resetData();
  }, [appliedFilters.country, appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    if (opSiteAreaToggle) resetData();
  }, [appliedFilters.level, appliedFilters.levelVal]);

  useEffect(() => {
    setOperatorSiteTooltipData();
  }, [operatorSiteTowers.length]);

  useEffect(() => {
    if (common_cond) {
      filterOperatorSites();
      saveDataLoadedBbox();
    }
    else if (!eyeIcons.sites || !operatorSiteVisible) resetData();
    // eslint-disable-next-line
  }, [
    eyeIcons.sites,
    selectedOperatorSiteOperator,
    appliedFilters,
    operatorSiteVisible,
    opSiteAreaToggle,
    siteZoom,
    geohashSW,
    boundsChanged
  ]);

  useEffect(() => {
    if (common_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      filterOperatorSites();
      saveDataLoadedBbox();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  const common_cond = useMemo(() => {
    return eyeIcons.sites && operatorSiteVisible && (mapZoomLevel >= siteZoom || mapZoomLevel >= clusterDefaultZoomLvl);
  }, [eyeIcons.sites, operatorSiteVisible, mapZoomLevel, siteZoom]);

  const resetData = () => {
    setOperatorSiteTowers([]);
  }

  const saveDataLoadedBbox = () => {
    const { supply, demand, capacity } = eyeIcons;
    // save data loaded bounds
    // do not save when any geo section on & map is about to load geohashes
    if (!((supply || demand || capacity) && geohashZoom && (mapZoomLevel === geohashZoom - 1))) saveLastBound();
  }

  const filterOperatorSites = () => {
    setOperatorSiteTowers([]);
    if (typeof cancelToken != typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(timeOutForOperatorSiteTowers.current);

    timeOutForOperatorSiteTowers.current = setTimeout(async () => {
      try {
        const { neBound, swBound } = getBbox;

        const payload = {
          ...appliedFilters,
          adL:
            opSiteAreaToggle && appliedFilters.level !== "geoHashLevel"
              ? appliedFilters.level
              : null,
          adLVal:
            opSiteAreaToggle && appliedFilters.level !== "geoHashLevel"
              ? appliedFilters.levelVal
              : null,
          operator: selectedOperatorSiteOperator,
          northEast: convertArrayToStringWithDelimeter(
            opSiteAreaToggle && geohashNE.length ? geohashNE : neBound,
            "_"
          ),
          southWest: convertArrayToStringWithDelimeter(
            opSiteAreaToggle && geohashSW.length ? geohashSW : swBound,
            "_"
          ),
        };

        await paginatedApiInvoker(
          (pageSize, page) => filterOperatorSiteTowers(payload, cancelToken, pageSize, page),
          40000, // perPage
          () => setApisOngoing(count => count + 1),
          (data) => {
            if (data) setOperatorSiteTowers(prevState => [...prevState, ...data]);
          },
          (data) => {
             //Saving filters for download
             setDownloadData({
               ...downloadData,
               sitesOperator: {
                 ...payload,
                 cancelToken: cancelToken,
               },
             });
            if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
            setApisOngoing(count => count - 1);
          }
        );
      } catch (err) {
        console.log("Error while filtering site towers", err);
        toast.error(err.message, { theme: "colored" });
      }
    }, timeOutForApiCall);
  };

  return (
    eyeIcons.sites && operatorSiteVisible && operatorSiteTowers.length > 0
      ? <div className="data-on-map">
        {mapZoomLevel >= siteZoom
          ? operatorSiteTowers.map((item, idx) => (
            <SiteIcon
              idx={idx}
              item={item}
              operatorDetails={operatorsPermittedOpSite}
              optr={selectedOperatorSiteOperator}
              setTooltip={setOperatorSiteTooltipData}
              mapTool={mapTool}
            />
          ))
          : mapZoomLevel >= clusterDefaultZoomLvl
            ? <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
              {(clusterer) => {
                return (
                  <>{operatorSiteTowers.map((item, idx) => (
                    <SiteIcon
                      idx={idx}
                      item={item}
                      operatorDetails={operatorDetails}
                      optr={selectedOperatorSiteOperator}
                      setTooltip={setOperatorSiteTooltipData}
                      mapTool={mapTool}
                      cluster={clusterer}
                    />
                  ))}</>
                )
              }}
            </MarkerClusterer>
            : <></>}
      </div>
      : <></>
  )
};

export default memo(OperatorSitesSection);